import { useState, useEffect } from 'react'
import { 
  MagnifyingGlass, 
  Plus, 
  DotsThreeVertical,
  User,
  Envelope,
  Phone,
  Calendar,
  Eye,
  PencilSimple,
  Trash,
  Download,
  Funnel
} from 'phosphor-react'
import { Menu } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE_URL = config.API_URL

interface User {
  id: string
  name: string
  email: string
  phone?: string
  plan?: string
  status?: string
  joinedAt?: string
  lastLogin?: string
  avatar?: string
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [planFilter, setPlanFilter] = useState('all')
  const [loading, setLoading] = useState(false)
  const [showUserModal, setShowUserModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    filterUsers()
  }, [searchTerm, statusFilter, planFilter, users])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('admin_token')
      const res = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await res.json()
      // Adaptar para o formato esperado pela tabela
      const usersAdapted = data.users.map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        plan: u.subscriptionStatus === 'active' ? 'Premium' : 'Básico',
        status: u.isActive ? 'Ativo' : 'Inativo',
        joinedAt: u.createdAt ? u.createdAt.split('T')[0].split('-').reverse().join('/') : '',
        lastLogin: u.updatedAt ? u.updatedAt.split('T')[0].split('-').reverse().join('/') : '',
        avatar: undefined // Adapte se tiver avatar
      }))
      setUsers(usersAdapted)
      setFilteredUsers(usersAdapted)
    } catch (e) {
      toast.error('Erro ao buscar usuários')
    } finally {
      setLoading(false)
    }
  }

  const filterUsers = () => {
    let filtered = users

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter)
    }

    if (planFilter !== 'all') {
      filtered = filtered.filter(user => user.plan === planFilter)
    }

    setFilteredUsers(filtered)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'badge-success'
      case 'Pendente':
        return 'badge-warning'
      case 'Inativo':
        return 'badge-error'
      case 'Bloqueado':
        return 'badge-error'
      default:
        return 'badge-info'
    }
  }

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'Premium':
        return 'bg-purple-100 text-purple-800'
      case 'Pro':
        return 'bg-blue-100 text-blue-800'
      case 'Básico':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleStatusChange = (userId: string, newStatus: User['status']) => {
    setUsers(prev => prev.map(user => 
      user.id === userId ? { ...user, status: newStatus } : user
    ))
    toast.success(`Status do usuário atualizado para ${newStatus}`)
  }

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      setUsers(prev => prev.filter(user => user.id !== userId))
      toast.success('Usuário excluído com sucesso')
    }
  }

  const handleExportUsers = () => {
    const csvContent = [
      ['Nome', 'Email', 'Telefone', 'Plano', 'Status', 'Data de Cadastro', 'Último Login'].join(','),
      ...filteredUsers.map(user => [
        user.name,
        user.email,
        user.phone || '',
        user.plan,
        user.status,
        user.joinedAt,
        user.lastLogin || ''
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    
    toast.success('Relatório de usuários exportado com sucesso!')
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-600">Gerencie todos os usuários da plataforma</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleExportUsers}
            className="btn btn-secondary btn-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
          <button className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4 mr-2" />
            Novo Usuário
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card card-body">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar usuários..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-full sm:w-48"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos os status</option>
            <option value="Ativo">Ativo</option>
            <option value="Inativo">Inativo</option>
            <option value="Pendente">Pendente</option>
            <option value="Bloqueado">Bloqueado</option>
          </select>
          <select
            className="input w-full sm:w-48"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="all">Todos os planos</option>
            <option value="Básico">Básico</option>
            <option value="Premium">Premium</option>
            <option value="Pro">Pro</option>
          </select>
        </div>
      </div>

      {/* Tabela de Usuários */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th>Usuário</th>
                <th>Plano</th>
                <th>Status</th>
                <th>Cadastro</th>
                <th>Último Login</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <User className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        {user.phone && (
                          <p className="text-xs text-gray-400">{user.phone}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getPlanColor(user.plan || '')}`}>
                      {user.plan}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusColor(user.status || '')}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {user.joinedAt ? new Date(user.joinedAt).toLocaleDateString('pt-BR') : ''}
                    </div>
                  </td>
                  <td>
                    {user.lastLogin ? (
                      <div className="text-sm text-gray-500">
                        {new Date(user.lastLogin).toLocaleDateString('pt-BR')}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Nunca</span>
                    )}
                  </td>
                  <td>
                    <Menu as="div" className="relative">
                      <Menu.Button className="p-2 text-gray-400 hover:text-gray-600">
                        <DotsThreeVertical className="h-5 w-5" />
                      </Menu.Button>
                      <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setShowUserModal(true)
                              }}
                              className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Visualizar
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                            >
                              <PencilSimple className="h-4 w-4 mr-2" />
                              Editar
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleStatusChange(user.id, user.status === 'Ativo' ? 'Inativo' : 'Ativo')}
                              className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                            >
                              {user.status === 'Ativo' ? 'Desativar' : 'Ativar'}
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-red-700`}
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              Excluir
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card card-body">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{users.length}</p>
            <p className="text-sm text-gray-600">Total de Usuários</p>
          </div>
        </div>
        <div className="card card-body">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {users.filter(u => u.status === 'Ativo').length}
            </p>
            <p className="text-sm text-gray-600">Usuários Ativos</p>
          </div>
        </div>
        <div className="card card-body">
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">
              {users.filter(u => u.plan === 'Premium').length}
            </p>
            <p className="text-sm text-gray-600">Usuários Premium</p>
          </div>
        </div>
        <div className="card card-body">
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {users.filter(u => u.status === 'Pendente').length}
            </p>
            <p className="text-sm text-gray-600">Usuários Pendentes</p>
          </div>
        </div>
      </div>
    </div>
  )
} 